import { useUserPreference } from '@/hooks/use-user-preference'
import { type IPagination } from '@/interface/pagination.interface'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Parse a URL param as a positive integer, falling back to defaultValue when missing/invalid
const parsePositiveIntParam = (value: string | null, defaultValue: number): number => {
  if (value === null || !/^\d+$/.test(value)) return defaultValue
  const parsed = parseInt(value, 10)
  return parsed > 0 ? parsed : defaultValue
}

// Get initial values from URL params or use defaults
export const getInitialPagination = (): IPagination => {
  const urlParams = new URLSearchParams(window.location.search)

  return {
    currentPage: parsePositiveIntParam(urlParams.get('page'), 1),
    perPage: parsePositiveIntParam(urlParams.get('perPage'), 10),
    searchTerm: urlParams.get('search') || '',
    total: parsePositiveIntParam(urlParams.get('total'), 1),
    totalPages: parsePositiveIntParam(urlParams.get('totalPages'), 1),
    refreshTable: false,
  }
}

export type UsePaginationResult = [IPagination, React.Dispatch<React.SetStateAction<IPagination>>, boolean]

const usePagination = (preferenceKey?: string): UsePaginationResult => {
  const [pagination, setPagination] = useState<IPagination>(getInitialPagination)
  const { userPreference, isLoading, userPreferences, upsertUserPreference } = useUserPreference(preferenceKey)
  const [isPreferenceReady, setIsPreferenceReady] = useState(!preferenceKey)
  const hasAppliedInitialPreference = useRef(false)

  const updateURLParams = (newPagination: IPagination) => {
    const urlParams = new URLSearchParams(window.location.search)

    if (newPagination?.currentPage > 1) urlParams.set('page', newPagination?.currentPage.toString())
    else urlParams.delete('page')

    if (newPagination?.perPage !== 10) urlParams.set('perPage', newPagination?.perPage.toString())
    else urlParams.delete('perPage')

    if (newPagination?.searchTerm) urlParams.set('search', newPagination?.searchTerm)
    else urlParams.delete('search')

    const newURL = `${window.location.pathname}${urlParams?.toString() ? '?' + urlParams?.toString() : ''}`
    // Keep React Router's history state (idx/key). Replacing it with {} makes later back steps leave the current area.
    window.history.replaceState(window.history.state, '', newURL)
  }

  const setPaginationWithURLUpdate = (value: React.SetStateAction<IPagination>) => {
    setPagination((prevPagination) => {
      const newPagination = typeof value === 'function' ? value(prevPagination) : value
      updateURLParams(newPagination)

      // Persist manual per-page changes per table
      if (preferenceKey && hasAppliedInitialPreference.current && newPagination.perPage !== prevPagination.perPage)
        void upsertUserPreference(preferenceKey, newPagination.perPage.toString(), true)

      return newPagination
    })
  }

  // Load saved per-table perPage before the first API fetch
  useLayoutEffect(() => {
    if (!preferenceKey || hasAppliedInitialPreference.current) return

    const listPreference = userPreferences?.find((p) => p.key === preferenceKey)
    const savedValue = userPreference?.value ?? listPreference?.value

    // Wait until preference fetch completes (use list cache when available)
    if (isLoading && savedValue === undefined) return

    hasAppliedInitialPreference.current = true

    const urlParams = new URLSearchParams(window.location.search)
    const hasExplicitUrlPerPage = urlParams.has('perPage')

    if (savedValue && !hasExplicitUrlPerPage) {
      const preferredPerPage = parseInt(savedValue, 10)
      if (!Number.isNaN(preferredPerPage))
        setPagination((prev) => {
          if (prev.perPage === preferredPerPage) return prev
          const next = { ...prev, perPage: preferredPerPage }
          updateURLParams(next)
          return next
        })
    }

    setIsPreferenceReady(true)
  }, [preferenceKey, isLoading, userPreference, userPreferences])

  useEffect(() => {
    if (pagination.totalPages > 0 && pagination.currentPage > pagination.totalPages)
      setPaginationWithURLUpdate((prev) => ({
        ...prev,
        currentPage: pagination.totalPages,
      }))
    else if (pagination.totalPages === 0 && pagination.currentPage > 1)
      setPaginationWithURLUpdate((prev) => ({
        ...prev,
        currentPage: 1,
      }))
  }, [pagination.totalPages, pagination.currentPage])

  useEffect(() => {
    const handlePopState = () => {
      setPagination(getInitialPagination())
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  return [pagination, setPaginationWithURLUpdate, isPreferenceReady]
}

export default usePagination
